// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Utility functions for working with prompts in the format "analysis_type/prompt_name"
 */

export interface GroupedPrompts {
  [analysisType: string]: string[];
}

/**
 * Groups prompts by their analysis type prefix
 * Expects prompts in format: "analysis_type/prompt_name"
 *
 * @param prompts - Array of prompt paths
 * @returns Object with analysis types as keys and prompt names as values
 */
export function groupPromptsByAnalysisType(prompts: string[]): GroupedPrompts {
  return prompts.reduce((acc, prompt) => {
    const parts = prompt.split('/');
    if (parts.length === 2) {
      const [type, name] = parts;
      if (!acc[type]) acc[type] = [];
      acc[type].push(name);
    } else {
      // Unexpected format - log warning
      console.warn(`Prompt not in expected format (analysis_type/name): ${prompt}`);
    }
    return acc;
  }, {} as GroupedPrompts);
}

/**
 * Parses a prompt path into its components
 *
 * @param promptPath - Full prompt path (e.g., "exam/detailed_analysis")
 * @returns Object with analysisType and promptName, or null if invalid
 */
export function parsePromptPath(promptPath: string): { analysisType: string; promptName: string } | null {
  const parts = promptPath.split('/');
  if (parts.length === 2) {
    return {
      analysisType: parts[0],
      promptName: parts[1],
    };
  }
  return null;
}

/**
 * Builds a full prompt path from components
 *
 * @param analysisType - The analysis type
 * @param promptName - The prompt name
 * @returns Full prompt path (e.g., "exam/detailed_analysis")
 */
export function buildPromptPath(analysisType: string, promptName: string): string {
  return `${analysisType}/${promptName}`;
}

/**
 * Extracts just the prompt name from a full path
 * If the path doesn't contain '/', returns the original string
 *
 * @param promptPath - Full prompt path or just a name
 * @returns The prompt name without the analysis type prefix
 */
export function extractPromptName(promptPath: string): string {
  const parts = promptPath.split('/');
  return parts.length === 2 ? parts[1] : promptPath;
}

/**
 * Extracts just the analysis type from a full path
 *
 * @param promptPath - Full prompt path
 * @returns The analysis type, or undefined if not in expected format
 */
export function extractAnalysisType(promptPath: string): string | undefined {
  const parts = promptPath.split('/');
  return parts.length === 2 ? parts[0] : undefined;
}

/**
 * Gets all unique analysis types from a list of prompts
 *
 * @param prompts - Array of prompt paths
 * @returns Sorted array of unique analysis types
 */
export function getAnalysisTypes(prompts: string[]): string[] {
  const types = new Set<string>();
  prompts.forEach((prompt) => {
    const type = extractAnalysisType(prompt);
    if (type) types.add(type);
  });
  return Array.from(types).sort();
}